import React from "react";
import { Link } from "react-router-dom";
import IntroVideo from '../videos/Axis_background.mp4';
import Axis_Logo from '../images/Axis_image6.png';
import VideoComponent from './VideoComponent.js';
import PageSEO from "../components/seo/PageSEO";
import { useScrollBehavior } from './useScrollBehavior.js';
import "../styles/home.css";

const col = { flexDirection: 'column', alignItems: 'center', justifyContent: 'center' };

export default function About() {
  useScrollBehavior();

  return (
    <main className="home">
      <PageSEO
        title="About | The Axis"
        description="The Axis — a secular ethical philosophy and organization built around reverence for Life, the Planet, and the Social Contract."
        path="/about"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "About The Axis",
          "url": (process.env.REACT_APP_SITE_URL || "") + "/about"
        }}
      />

      <div className="home__bg" aria-hidden="true">
        <VideoComponent videoSrc={IntroVideo} />
      </div>

      <section className="home__content">

        {/* ── HERO ── */}
        <div className="module" style={col}>
          <div className='home__logo '>
            <img src={Axis_Logo} alt="Axis_logo" className="axis_logo" />
          </div>
          <span className="about__eyebrow">The Axis</span>
          <h1 className="home__title">About</h1>
          <p className="home__subtitle">A Philosophy. An Organization. A Platform.</p>
          <p className="home__copy">
            The Axis is a secular ethical framework built around three principles
            we hold to be non-negotiable: Life, the Planet, and the Social Contract.
            It is also an organization of people who have accepted that those principles
            demand more than passive agreement — they demand action.
          </p>
          <p className="home__copy" style={{ marginTop: '1rem' }}>
            This page is for anyone who wants to understand what the Axis is,
            why it exists, and whether it is something they want to be part of.
          </p>
        </div>

        {/* ── WHY THE AXIS EXISTS ── */}
        <div className="module" style={col}>
          <div className="about__split" style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
            <div className="about__text">
              <span className="about__eyebrow">Context</span>
              <h2 className="about__heading">Why This. Why Now.</h2>
              <p className="about__body">
                We are not living through ordinary times. The systems that govern modern
                life — political, economic, ecological, and social — are failing in ways
                that are measurable, documented, and accelerating. The institutions built
                to address these failures are either captured by the interests driving them
                or too slow to respond at the speed the problems demand.
              </p>
              <p className="about__body">
                The Axis does not begin from a place of despair. It begins from a place of
                clarity: the destruction of Life, the Planet, and the Social Contract is
                not inevitable — but it will not be stopped by those who profit from it.
                It will be stopped by people who see it clearly, who share a common ethical
                language, and who are willing to act together.
              </p>
              <p className="about__body">
                That is what the Axis is for.
              </p>
            </div>
            <div className="about__stats">
              <div className="stat">
                <span className="stat__number">3</span>
                <span className="stat__label">Sacred Principles</span>
              </div>
              <div className="stat">
                <span className="stat__number">4</span>
                <span className="stat__label">Steps to Change</span>
              </div>
              <div className="stat">
                <span className="stat__number">1</span>
                <span className="stat__label">Shared Purpose</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── THE SACRED THREE ── */}
        <div className="module" style={col}>
          <h2 className="module__heading">The Sacred Three</h2>
          <p className="module__subheading">
            Three things that are not negotiable. Not political positions, not preferences
            — principles. The Axis is built on what happens when you take them seriously.
          </p>
          <div className="pillars__grid">

            <div className="pillar">
              <div className="pillar__icon" aria-hidden="true">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2"/>
                  <path d="M24 14c0 0-8 6-8 12a8 8 0 0016 0c0-6-8-12-8-12z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                  <line x1="24" y1="34" x2="24" y2="38" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="pillar__title">Life</h3>
              <p className="pillar__body">
                All living things have inherent worth. Human life, animal life, the
                biological systems that sustain existence — these are not resources to be
                optimized or sacrificed for profit. Reverence for life is the first and
                most fundamental principle of the Axis. It means opposing — actively,
                not rhetorically — anything that treats living beings as expendable.
              </p>
            </div>

            <div className="pillar">
              <div className="pillar__icon" aria-hidden="true">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M24 6C14 6 6 14 6 24s8 18 18 18 18-8 18-18S34 6 24 6z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M6 24h36M24 6c-4 4-6 10-6 18s2 14 6 18M24 6c4 4 6 10 6 18s-2 14-6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="pillar__title">The Planet</h3>
              <p className="pillar__body">
                The natural world is not a backdrop to human activity — it is the system
                that makes all life possible. The Axis holds that environmental destruction
                is not an acceptable trade-off for economic growth, political convenience,
                or short-term comfort. Protecting the planet is not an optional add-on to
                ethical living. It is central to it.
              </p>
            </div>

            <div className="pillar">
              <div className="pillar__icon" aria-hidden="true">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="16" cy="18" r="6" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="32" cy="18" r="6" stroke="currentColor" strokeWidth="2"/>
                  <path d="M4 38c0-6.627 5.373-10 12-10M44 38c0-6.627-5.373-10-12-10M24 38c0-6.627 2.686-10 0-10s0 3.373 0 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M20 28c1.2-.6 2.5-.9 4-.9s2.8.3 4 .9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="pillar__title">The Social Contract</h3>
              <p className="pillar__body">
                We exist in relation to one another. The Social Contract is the
                architecture of mutual obligation — the understanding that a functioning
                society depends on people taking their responsibilities to each other
                seriously. When that contract breaks down, everything else follows.
                Rebuilding and defending it is not idealism — it is survival.
              </p>
            </div>

          </div>
        </div>

        {/* ── THE MISSION ── */}
        <div className="module" style={col}>
          <div className="panel">
            <div className="panel__title">The Mission</div>
            <p className="module__subheading">
              The Axis does not exist to hold opinions. It exists to act on them.
              The mission moves through four stages — each one builds on the last.
            </p>
            <div className="panel__body">
              <div className="panel__row">
                <div className="panel__label">01 — Change Yourself</div>
                <div className="panel__value">
                  The work begins internally. To become conscious of the Sacred Three
                  and to live accordingly — in your decisions, your relationships, your
                  work, and your consumption. You cannot organize what you have not
                  first embodied. This stage is not a preparation phase. It is permanent
                  and ongoing.
                </div>
              </div>
              <div className="panel__row">
                <div className="panel__label">02 — Act</div>
                <div className="panel__value">
                  Awareness without action is surrender. The second stage is to move —
                  locally, practically, and consistently. To engage in the community,
                  to protect what is sacred in your immediate sphere, and to demonstrate
                  that ethical commitment is not a belief system but a practice.
                </div>
              </div>
              <div className="panel__row">
                <div className="panel__label">03 — Organize</div>
                <div className="panel__value">
                  No one sustains meaningful action alone for long. The third stage is
                  to find others — people who share the same ethical foundation — and to
                  build with them. Structure, accountability, mutual support, and shared
                  purpose. This is where individual commitment becomes collective force.
                </div>
              </div>
              <div className="panel__row">
                <div className="panel__label">04 — Act as One</div>
                <div className="panel__value">
                  The final stage is to become a force capable of protecting what is
                  sacred at scale. Coordinated, principled, and effective — on behalf
                  of the Sacred Three. Not theoretical. Not eventual. Now, and with
                  the full weight of a unified organization behind it.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── THE ORGANIZATION ── */}
        <div className="module" style={col}>
          <h2 className="module__heading">The Organization</h2>
          <p className="module__subheading">
            The Axis is a membership organization — not a media platform or a movement
            in name only. Membership means something.
          </p>
          <div className="features__grid">
            <div className="feature">
              <div className="feature__icon" aria-hidden="true">
                <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="16" cy="10" r="5" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M6 26c0-5.523 4.477-8 10-8s10 2.477 10 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <h4 className="feature__title">Membership</h4>
              <p className="feature__body">
                You join the Axis by committing to its principles and participating in
                its structure. There are no passive members. Everyone who is part of the
                Axis is expected to contribute — to the community, to the mission, and
                to their own ongoing development.
              </p>
            </div>
            <div className="feature">
              <div className="feature__icon" aria-hidden="true">
                <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 6h24v20H4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                  <path d="M8 11h16M8 16h10M8 21h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <h4 className="feature__title">The Codex</h4>
              <p className="feature__body">
                The Axis philosophy is documented in the Codex — a living body of work
                that defines the principles, their application, and the reasoning behind
                them. The book is the public-facing entry point. The Codex is the full
                framework that members study and apply.
              </p>
            </div>
            <div className="feature">
              <div className="feature__icon" aria-hidden="true">
                <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M26 6H6a2 2 0 00-2 2v12a2 2 0 002 2h14l6 4V8a2 2 0 00-2-2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                </svg>
              </div>
              <h4 className="feature__title">The Platform</h4>
              <p className="feature__body">
                The Axis app is the operational layer — where members communicate,
                organize, share information, and coordinate action. It is designed from
                the ground up with the Sacred Three embedded in its architecture, not
                bolted on afterward.
              </p>
            </div>
            
          </div>
        </div>

        {/* ── WHO IT'S FOR ── */}
        <div className="module" style={col}>
          <h2 className="module__heading">Who the Axis Is For</h2>
          <p className="module__subheading">
            Not everyone. But if any of this describes you, keep reading.
          </p>
          <div className="pillars__grid">
            <div className="pillar">
              <h3 className="pillar__title">The Thinker</h3>
              <p className="pillar__body">
                You have been looking for a coherent ethical framework — one that does
                not require religion, does not reduce to politics, and does not flinch
                from hard questions. The Axis provides that framework and the community
                to sharpen it against.
              </p>
            </div>
            <div className="pillar">
              <h3 className="pillar__title">The Builder</h3>
              <p className="pillar__body">
                You create things — technology, organizations, content, businesses —
                and you are tired of doing it without a coherent ethical foundation.
                You want to build in alignment with something real, and to connect
                with others doing the same.
              </p>
            </div>
            <div className="pillar">
              <h3 className="pillar__title">The Actor</h3>
              <p className="pillar__body">
                Philosophy is useful to you only insofar as it produces action.
                You are not here to debate — you are here to move. The Axis gives
                your energy a structure within which it can be directed effectively,
                and people alongside whom you can act.
              </p>
            </div>
          </div>
        </div>

        {/* ── CLOSING CTA ── */}
        <div className="module" style={col}>
          <div className="panel panel--closing">
            <div className="panel__title">Where to Go From Here</div>
            <div className="panel__body">
              <div className="panel__row">
                <div className="panel__label">Read</div>
                <div className="panel__value">
                  The book is the entry point into the Axis philosophy. Start there.
                </div>
              </div>
              <div className="panel__row">
                <div className="panel__label">Join</div>
                <div className="panel__value">
                  If the Axis is what you have been looking for, join the organization
                  and begin the work.
                </div>
              </div>
              <div className="panel__row">
                <div className="panel__label">Connect</div>
                <div className="panel__value">
                  Get on the platform. Meet the people. See what is being built.
                </div>
              </div>
            </div>
            <div className="panel__cta">
              <Link className="btn btn--primary" to="/join">Join the Axis</Link>
              <Link className="btn btn--ghost" to="/book">Get the Book</Link>
              <Link className="btn btn--ghost" to="/app">Get the App</Link>
            </div>
          </div>
        </div>

      </section>
    </main>
  );
}


/*
<div className="feature">
              <div className="feature__icon" aria-hidden="true">
                <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 4l3.09 6.26L26 11.27l-5 4.87 1.18 6.88L16 19.77l-6.18 3.25L11 16.14 6 11.27l6.91-1.01L16 4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                </svg>
              </div>
              <h4 className="feature__title">Axis Tactical</h4>
              <p className="feature__body">
                For members who choose it, Axis Tactical is the physical and operational
                arm of the organization — covering combatives, weapons, tactical medicine,
                and the ethical framework governing their use. Participation is by
                invitation only.
              </p>
            </div>


import React from "react";
import { Link } from "react-router-dom";
import PageSEO from "../components/seo/PageSEO";
import "../styles/page.css";
import "../styles/product.css";
import { useScrollBehavior } from './useScrollBehavior.js';

export default function About() {

  useScrollBehavior();

  return (
    <main className="page">
      <PageSEO
        title="About | Your App Name"
        description="What this platform is, what it does, and where it's going."
        path="/about"
      />
      <header className="pageHeader">
        <h1>About</h1>
        <p>High-level overview. Replace this with your real copy.</p>
      </header>

      <section className="section">
        <div className="card">
          <h2>Core features</h2>
          <ul className="bullets">
            <li>Posts with images and text</li>
            <li>Likes + emoji reactions</li>
            <li>Direct messages</li>
            <li>Admin news</li>
          </ul>

          <div className="ctaRow">
            <Link className="btn btn--primary" to="/app">Get the App</Link>
            <Link className="btn btn--ghost" to="/book">Get the Book</Link>
            <Link className="btn btn--ghost" to="/contact">Contact</Link>
          </div>
        </div>
      </section>
    </main>
  );
}*/
